import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FileText, FileSpreadsheet, FileArchive, Download, Loader2, Eye, EyeOff, Send, Lock } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { useAuditExports } from '@/hooks/useAudit';
import { callGenerateExport, callSetExportPublished, callSignedDownload } from '@/lib/functions';
import { fmtBytes, fmtDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cardClassName } from '@/components/ui/card';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import type { messages as MSG } from '@/lib/i18n.messages';
import type { AuditDoc, ExportDoc } from '@/types/models';

type TKey = keyof typeof MSG;
const TYPES: { id: 'pdf_executive' | 'pdf_full' | 'excel' | 'zip_bundle'; labelKey: TKey; descKey: TKey; icon: React.ReactNode }[] = [
  { id: 'pdf_executive', labelKey: 'reports.type.pdf_exec', descKey: 'reports.type.pdf_exec_d', icon: <FileText className="h-5 w-5" /> },
  { id: 'pdf_full',      labelKey: 'reports.type.pdf_full', descKey: 'reports.type.pdf_full_d', icon: <FileText className="h-5 w-5" /> },
  { id: 'excel',         labelKey: 'reports.type.excel',    descKey: 'reports.type.excel_d',    icon: <FileSpreadsheet className="h-5 w-5" /> },
  { id: 'zip_bundle',    labelKey: 'reports.type.zip',      descKey: 'reports.type.zip_d',      icon: <FileArchive className="h-5 w-5" /> },
];

export function AdminReportsSection({ audit }: { audit: AuditDoc }) {
  const { t, locale } = useT();
  const exports_ = useAuditExports(audit.id);
  const [generating, setGenerating] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  // Refresh tick after generate so the watcher picks up the new export
  const [, setTick] = useState(0);
  useEffect(() => { setTick(x => x + 1); }, [generating]);

  const generate = async (type: typeof TYPES[number]['id']) => {
    setGenerating(type);
    try {
      await callGenerateExport({ auditId: audit.id, type, locale });
      toast.success(t('reports.generated'));
    } catch (e) { toast.error((e as Error).message); }
    finally { setGenerating(null); }
  };

  const togglePublish = async (e: ExportDoc) => {
    setPublishingId(e.id);
    try {
      await callSetExportPublished({
        auditId: e.auditId, exportId: e.id, published: !e.publishedToClient,
      });
      toast.success(t(e.publishedToClient ? 'reports.admin.unpublished_toast' : 'reports.admin.published_toast'));
    } catch (err) { toast.error((err as Error).message); }
    finally { setPublishingId(null); }
  };

  const download = async (e: ExportDoc) => {
    try {
      const { data } = await callSignedDownload({ kind: 'export', path: e.filePath });
      window.location.href = data.url;
    } catch (err) { toast.error((err as Error).message); }
  };

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/30">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          <Lock className="h-4 w-4" /> {t('reports.admin.section')}
        </div>
        <div className="mt-1 text-xs text-amber-800 dark:text-amber-300">
          {t('reports.admin.section.desc')}
        </div>
      </div>

      {/* Generation buttons */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {TYPES.map(et => (
          <button
            key={et.id}
            onClick={() => generate(et.id)}
            disabled={generating !== null}
            className={cardClassName('flex items-start gap-4 text-left disabled:opacity-60')}
          >
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
              {generating === et.id ? <Loader2 className="h-5 w-5 animate-spin" /> : et.icon}
            </div>
            <div className="flex-1">
              <div className="font-medium text-ink-900 dark:text-white">{t(et.labelKey)}</div>
              <div className="text-xs text-ink-500">{t(et.descKey)}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Generated exports */}
      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">
          {t('reports.history')}
        </h3>
        {exports_.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-ink-500 dark:border-border dark:bg-muted/40">
            {t('reports.admin.no_exports')}
          </div>
        ) : (
          <Table>
            <TableHead>
              <tr>
                <TableHeadCell>{t('reports.col.type')}</TableHeadCell>
                <TableHeadCell>{t('common.date')}</TableHeadCell>
                <TableHeadCell>{t('reports.col.size')}</TableHeadCell>
                <TableHeadCell>{t('common.status')}</TableHeadCell>
                <TableHeadCell className="text-right">{t('common.actions')}</TableHeadCell>
              </tr>
            </TableHead>
            <TableBody>
              {exports_.map(e => {
                const labelKey = TYPES.find(x => x.id === e.type)?.labelKey;
                return (
                  <TableRow key={e.id} className={publishingId === e.id ? 'opacity-50' : ''}>
                    <TableCell>{labelKey ? t(labelKey) : e.type}</TableCell>
                    <TableCell className="text-ink-500">{fmtDate(e.createdAt)}</TableCell>
                    <TableCell className="text-ink-500">{fmtBytes(e.sizeBytes)}</TableCell>
                    <TableCell>
                      {e.publishedToClient ? (
                        <Badge variant="success" className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {t('reports.admin.published')}</Badge>
                      ) : (
                        <Badge variant="neutral" className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> {t('reports.admin.unpublished')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <Button variant="ghost" onClick={() => download(e)} title={t('reports.download')}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <button
                          onClick={() => togglePublish(e)}
                          disabled={publishingId !== null}
                          className={
                            e.publishedToClient
                              ? 'inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-600 transition hover:bg-slate-50 dark:border-border dark:bg-card dark:text-foreground/80'
                              : 'inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-700'
                          }
                        >
                          {publishingId === e.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : e.publishedToClient
                              ? <><EyeOff className="h-3.5 w-3.5" /> {t('reports.admin.unpublish')}</>
                              : <><Send className="h-3.5 w-3.5" /> {t('reports.admin.publish')}</>}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  );
}
